/**
 * useTypewriter - drives a "type out" animation for streaming text.
 *
 * - Initialises `displayed` to `''` while streaming (`active=true`) so the
 *   animation always starts from the beginning, regardless of how much text
 *   has already arrived in the first render.
 * - Initialises to the full `target` for completed runs that mount from
 *   history (`active=false` on mount) - no animation needed.
 * - The interval re-evaluates on every character advance (`displayed` in
 *   deps) and stops only once `displayed` has caught up to `target` AND
 *   `active` is false.  This means the animation continues draining even
 *   after the server signals completion, so the full response is always
 *   typed out smoothly.
 * - `msPerChar` defaults to 30 ms (~33 chars/s) for a natural feel.
 */

import * as React from 'react';

export function useTypewriter(target: string, active: boolean, msPerChar = 50): string {
  // History runs (active=false on mount) display immediately; streaming
  // runs start from '' so the typewriter always animates from zero.
  const [displayed, setDisplayed] = React.useState<string>(() => (active ? '' : target));

  // Always-fresh refs so interval callbacks never close over stale values.
  const targetRef = React.useRef(target);
  const activeRef = React.useRef(active);
  React.useLayoutEffect(() => {
    targetRef.current = target;
    activeRef.current = active;
  });

  // Snap to full text the moment streaming ends so the user never waits for
  // the typewriter to drain after the server signals completion.
  React.useEffect(() => {
    if (!active) setDisplayed(targetRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  React.useEffect(() => {
    // Stop condition: fully caught up and streaming is over.
    if (displayed.length >= targetRef.current.length && !active) return;

    const id = setInterval(() => {
      setDisplayed((prev) => {
        const full = targetRef.current;
        if (prev.length >= full.length) return prev; // idle while waiting for next delta
        return full.slice(0, prev.length + 1);
      });
    }, msPerChar);

    return () => clearInterval(id);
    // Re-evaluate after every character advance or streaming-state change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayed, active, msPerChar]);

  return displayed;
}
